/* Bill Jewellers — Design Studio
   ─────────────────────────────────────────────────────────────────────
   Four choices, four concepts, then straight into a consultation with the
   brief already written. The generator is a conversation-starter, not a
   product configurator: nothing here quotes a price or promises a piece.

   Talks to server.py. With no OpenAI key configured the endpoint answers in
   mock mode and the UI says so plainly rather than pretending.
   ───────────────────────────────────────────────────────────────────── */
(() => {
  'use strict';

  /* Prefill the booking form if the customer arrived from the studio.
     This runs BEFORE the studio guard below — appointments.html loads this
     same file but has no [data-studio], so anything after the guard would
     never execute there. */
  /* Prefill from a product page's CTA (?piece=…&intent=…&opt=…): the piece
     and any option chip the customer picked travel into the message, so the
     enquiry the shop receives says WHAT it is about without a phone call.
     URL params, not storage — survives static hosting and shared links. */
  (function carryPiece() {
    const msg = document.querySelector('#msg');
    if (!msg || msg.value.trim()) return;
    try {
      const q = new URLSearchParams(location.search);
      const piece = (q.get('piece') || '').slice(0, 80);
      if (!piece) return;
      const opt = (q.get('opt') || '').slice(0, 120);
      const intent = q.get('intent') === 'enquiry' ? 'Enquiry about'
                                                   : 'Booking for';
      msg.value = intent + ': ' + piece + (opt ? ' (' + opt + ')' : '') + '\n\n';
    } catch (e) { /* old browser — the customer just types it */ }
  })();

  (function carryBrief() {
    const msg = document.querySelector('#msg');
    if (!msg || msg.value.trim()) return;
    try {
      const brief = sessionStorage.getItem('bj_brief');
      if (brief) {
        msg.value = brief + '\n\n';
        const field = msg.closest('.field');
        if (field) {
          const hint = document.createElement('p');
          hint.className = 'muted';
          hint.style.cssText = 'font-size:11px;letter-spacing:.1em;text-transform:uppercase';
          hint.textContent = 'Brought over from the Design Studio';
          field.appendChild(hint);
        }
      }
    } catch (e) { /* private mode — customer can paste it themselves */ }
  })();

  const root = document.querySelector('[data-studio]');
  if (!root) return;

  /* A FRESH studio run must not inherit the previous one's picked design.
     bj_picked is written when the customer shortlists a concept and read on
     the consultation page; nothing ever cleared it, so a second run in the
     same tab (very common — click through twice) sent the shop the first
     run's title and image stapled to the second run's words. Clearing it the
     moment the studio opens is the one place that covers every path in,
     including "Skip the drawings", which parks nothing of its own. */
  try { sessionStorage.removeItem('bj_picked'); } catch (e) {}

  const $ = (s, c = root) => c.querySelector(s);
  const $$ = (s, c = root) => [...c.querySelectorAll(s)];

  const state = {
    shape: null,
    material: 'Silver',
    // His third question is the CHAIN. There is no stone chooser on his
    // studio — pieces are iced-out, hand-set, full pavé as standard — so the
    // studio states that and asks nothing. `stone` is kept only because the
    // render prompt takes one; it is never a customer choice.
    chain: 'Tennis Chain',
    stone: 'moissanite',
    note: '',
    reference: null,      // data URL, already downscaled
    referenceName: '',
    concepts: [],
    chosen: new Set(),
    refining: null,
    mock: false,
  };

  const steps = $$('[data-step]');
  const statusEl = $('[data-studio-status]');

  /* Arrived from one of the homepage commission buttons (?piece=…): ONE
     funnel, James's call 31 Jul — the quick-apply modal no longer forks the
     journey. The piece rides into the note, and the one certain mapping
     (Custom Logo Pendant → his "Logo Pendant" shape) is preselected; any
     other piece leaves the shape choice to the customer rather than guess. */
  (function fromPiece() {
    let piece = '';
    try { piece = (new URLSearchParams(location.search).get('piece') || '').slice(0, 80); }
    catch (e) { return; }
    if (!piece) return;
    const note = $('[data-note]');
    if (note && !note.value.trim()) {
      note.value = 'Interested in: ' + piece + '. ';
      state.note = note.value;
      // The note is the ONLY thing gating [data-generate], and validateStep3
      // otherwise runs only from the note's own `input` event — which a
      // programmatic .value never fires. Without this call the customer
      // arrives from a homepage commission button, walks to step 3, and finds
      // "Draw four concepts" greyed out with text already in the box.
      validateStep3();
    }
    if (piece === 'Custom Logo Pendant') {
      // deferred one tick: wireGroup attaches the click handlers later in
      // this same script run, so an immediate click() lands on deaf ears
      setTimeout(() => {
        const btn = $$('[data-shape]').find((b) => b.dataset.value === 'Logo Pendant');
        if (btn) btn.click();
      }, 0);
    }
  })();

  /* ── step machine ─────────────────────────────────────────────────── */
  function go(n, opts) {
    /* Boot calls go(1) with scroll:false — a fresh landing must show the
       header and the page head, not jump 600px past them. */
    const moveView = !(opts && opts.scroll === false);
    steps.forEach((s) => {
      const active = Number(s.dataset.step) === n;
      s.hidden = !active;
      s.classList.toggle('is-active', active);
    });
    $$('[data-dot]').forEach((d) => {
      const i = Number(d.dataset.dot);
      d.classList.toggle('is-done', i < n);
      d.classList.toggle('is-current', i === n);
    });
    /* Scroll to the STEP, not the page head. Scrolling to the head put the
       eyebrow, H1, lede and banner back on screen after every single click —
       the next question was below the fold on every step, and on mobile the
       concepts loaded entirely off-screen while the visitor stared at the
       banner. Only scroll at all when the step's top is actually out of view. */
    const stepEl = $(`[data-step="${n}"]`) || $('[data-studio-top]');
    if (stepEl) {
      const top = stepEl.getBoundingClientRect().top;
      if (moveView && (top < 0 || top > 140)) {
        stepEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
      /* Every Continue/Back hides the button that was just clicked, which
         silently dumps keyboard focus onto <body> — the next Tab restarted
         from the page header on every single step. Land focus on the new
         step's own heading instead (the standard multi-step pattern). */
      const h = stepEl.querySelector('h2, h3');
      if (moveView && h) {
        h.setAttribute('tabindex', '-1');
        h.focus({ preventScroll: true });
      }
    }
    refreshSummary();
  }

  function label(group, value) {
    const el = $(`[data-${group}][value="${value}"]`) ||
               $$(`[data-${group}]`).find((b) => b.dataset.value === value);
    return el ? (el.dataset.label || el.textContent.trim()) : value;
  }

  function refreshSummary() {
    const bits = [];
    if (state.shape) bits.push(label('shape', state.shape));
    bits.push(label('chain', state.chain));
    bits.push(label('material', state.material));
    const el = $('[data-summary]');
    if (el) el.textContent = bits.filter(Boolean).join(' · ');
  }

  /* ── choice groups ────────────────────────────────────────────────── */
  function wireGroup(attr, key, onPick) {
    $$(`[${attr}]`).forEach((btn) => {
      btn.addEventListener('click', () => {
        $$(`[${attr}]`).forEach((b) => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        state[key] = btn.dataset.value;
        refreshSummary();
        if (onPick) onPick();
      });
    });
  }

  wireGroup('data-shape', 'shape', () => {
    const next = $('[data-next="2"]');
    if (next) next.disabled = false;
  });
  wireGroup('data-material', 'material');
  wireGroup('data-chain', 'chain');

  $$('[data-next]').forEach((b) =>
    b.addEventListener('click', () => go(Number(b.dataset.next))));
  $$('[data-back]').forEach((b) =>
    b.addEventListener('click', () => go(Number(b.dataset.back))));

  /* ── reference image ──────────────────────────────────────────────── */
  const drop = $('[data-drop]');
  const fileInput = $('[data-file]');
  const noteInput = $('[data-note]');

  if (noteInput) {
    noteInput.addEventListener('input', () => {
      state.note = noteInput.value;
      validateStep3();
    });
  }

  /* Phone photos are 4-12 MB. Downscaling in the browser keeps us well
     under the server ceiling and cuts the upload to something a shop's
     wifi can manage. 1024px is all the model uses anyway. */
  function downscale(file, max = 1024) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read that file.'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('That file is not an image we can read.'));
        img.onload = () => {
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/png'));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  async function takeFile(file) {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return setStatus('That needs to be an image file.', 'error');
    }
    // The drop zone promises "up to 8 MB" — hold the promise.
    if (file.size > 8 * 1024 * 1024) {
      return setStatus('That photo is over 8 MB — try a smaller one.', 'error');
    }
    setStatus('Reading your image…');
    try {
      state.reference = await downscale(file);
      state.referenceName = file.name;
      const prev = $('[data-preview]');
      if (prev) {
        prev.src = state.reference;
        prev.hidden = false;
      }
      $('[data-drop-label]').textContent = file.name;
      $('[data-clear-ref]').hidden = false;
      setStatus('');
      validateStep3();
    } catch (e) {
      setStatus(e.message, 'error');
    }
  }

  if (fileInput) {
    fileInput.addEventListener('change', (e) => takeFile(e.target.files[0]));
  }
  if (drop) {
    ['dragenter', 'dragover'].forEach((ev) =>
      drop.addEventListener(ev, (e) => {
        e.preventDefault();
        drop.classList.add('is-over');
      }));
    ['dragleave', 'drop'].forEach((ev) =>
      drop.addEventListener(ev, (e) => {
        e.preventDefault();
        drop.classList.remove('is-over');
      }));
    drop.addEventListener('drop', (e) => takeFile(e.dataTransfer.files[0]));
  }
  const clearRef = $('[data-clear-ref]');
  if (clearRef) {
    clearRef.addEventListener('click', () => {
      state.reference = null;
      state.referenceName = '';
      $('[data-preview]').hidden = true;
      $('[data-drop-label]').textContent = 'Drop an image, or browse';
      clearRef.hidden = true;
      if (fileInput) fileInput.value = '';
      validateStep3();
    });
  }

  function validateStep3() {
    const ok = Boolean(state.reference) || state.note.trim().length > 2;
    const btn = $('[data-generate]');
    if (btn) btn.disabled = !ok;
  }

  /* ── generate ─────────────────────────────────────────────────────── */
  function setStatus(msg, kind) {
    if (!statusEl) return;
    statusEl.textContent = msg || '';
    statusEl.hidden = !msg;
    statusEl.className = 'studio__status' + (kind ? ' is-' + kind : '');
  }

  /* The drawings are OPTIONAL (James, 31 Jul: one commission funnel — the
     customer chooses AI concepts or not). Skip lands on the brief step with
     their choices + words already composed; composeBrief() simply has no
     shortlist line when no concepts exist. */
  const skipBtn = $('[data-skip-generate]');
  if (skipBtn) {
    skipBtn.addEventListener('click', () => {
      saveBrief();
      go(5);
    });
  }

  const genBtn = $('[data-generate]');
  if (genBtn) {
    genBtn.addEventListener('click', async () => {
      go(4);
      renderSkeletons();
      genBtn.disabled = true;
      setStatus('Drawing your five concepts. About a minute each, all at once.');

      try {
        const res = await fetch((window.BJ_ENGINE||'')+'/api/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shape: state.shape,
            chain: state.chain,   // his six-step flow HAS a chain step; it was
                                  // collected and then never sent, so an entire
                                  // step of the owner's own designer was
                                  // decorative. Verified off the wire: prompts
                                  // carried no rope/cuban/tennis at all.
            material: state.material,
            stone: state.stone,
            note: state.note,
            reference: state.reference,
          }),
        });
        // A proxy timeout page or empty body is not JSON — parsing it raw
        // put "Unexpected token '<'…" in the customer's face instead of the
        // friendly line already sitting in the next expression.
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Something went wrong.');

        // Two response shapes. `concepts` is the art-directed one (title +
        // pitch, image optional); `images` is the plain/mock one.
        state.concepts = (data.concepts || []).length
          ? data.concepts
          : (data.images || []).map((src, i) => ({
              image: src, title: `Concept ${i + 1}`, pitch: '', spec: '',
            }));
        state.concepts.forEach((c) => {
          c.history = [{ image: c.image, prompt: c.prompt, change: null }];
          c.at = 0;
        });
        state.refining = null;
        state.brief = data.brief || '';
        state.mock = Boolean(data.mock);
        state.chosen.clear();
        renderConcepts();

        // Each drawing is its own job: reveal them one by one as they land.
        const pending = state.concepts.filter((c) => c.job && !c.image);
        if (pending.length) {
          setStatus(benchStatus());
          await Promise.all(pending.map(async (c) => {
            try { c.image = await waitForJob(c.job); }
            catch (e) { c.image = null; c.job = null; c.failed = true; }
            c.history = [{ image: c.image, prompt: c.prompt, change: null }];
            renderConcepts();
            setStatus(benchStatus());
          }));
          // drop the ones that failed so no empty tile ever faces a customer
          state.concepts = state.concepts.filter((c) => c.image);
          state.concepts.forEach((c, i) => { c.number = i + 1; });
          renderConcepts();
          if (!state.concepts.length) throw new Error('The studio could not draw just now. Try again in a minute, or book a consultation and we will sketch it with you.');
        }

        const noPictures = state.concepts.length &&
                           !state.concepts.some((c) => c.image);
        setStatus(
          state.mock
            ? 'Demo mode — these are existing pieces from the shop, not generated concepts.'
            : noPictures
              ? 'Written directions only — the image service isn’t connected yet. Pick the ones you like and we’ll sketch them at the consultation.'
              : '',
          (state.mock || noPictures) ? 'note' : ''
        );
      } catch (e) {
        renderConcepts();   // clears the skeletons
        setStatus(e.message, 'error');
      } finally {
        genBtn.disabled = false;
      }
    });
  }

  const grid = $('[data-concepts]');

  /* ── THE WAIT IS THE PROOF WALL ─────────────────────────────────────────
     The generation wait was four grey shimmer rectangles — sixty seconds of
     dead air at the studio's emotional peak. Instead, the shop shows its own
     portfolio while it "draws": real photographs of real pieces, cross-fading
     every 2.2s. They are captioned as the SHOP'S work, never as the
     customer's concepts — that distinction is the honesty rule this page
     lives by. Free content: the photos were already on the page's domain. */
  const PROOF_WALL = [
    ['cross-3-row-closeup',        "Three-row cross — every stone hand-set"],
    ['tennis-chain-rows-closeup',  "Tennis chain, rows of stones"],
    ['letter-pendant-s',           "Signature letter pendant"],
    ['custom-logo-pendant-square-tag', "A custom logo commission"],
    ['snowflake-pendant-closeup',  "The Snowflake"],
    ['cuban-bracelet-on-black',    "Cuban link bracelet"],
    ['hamsa-pendant-baguette',     "Hamsa hand, baguette centre"],
    ['spider-pendant-on-rose-chain', "Spider pendant on rose chain"],
  ];
  let proofTimer = null;

  /* ── the wait, as a thing to look at ──────────────────────────────
     A drawing takes about a minute. The customer watches the bench, not a
     spinner: pieces from the case cycle through the empty slots with a
     line about what is happening to theirs, and each drawing replaces its
     slot the second it lands. */
  const BENCH_LINES = [
    'Setting the stones', 'Shaping the metal', 'Polishing the surface',
    'Checking the sparkle', 'Finishing the edges', 'Nearly there',
  ];
  async function waitForJob(id, onTick) {
    const t0 = Date.now();
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      let st = {};
      try {
        st = await fetch((window.BJ_ENGINE||'')+'/api/job/'+encodeURIComponent(id)).then((r) => r.json());
      } catch (e) { st = {}; }
      if (st.status === 'done' && st.image) return st.image;
      if (st.status === 'failed') throw new Error(st.error || 'That drawing did not come out.');
      if (Date.now() - t0 > 290000) throw new Error('That drawing is taking too long. Try again.');
      if (onTick) onTick(Math.round((Date.now() - t0) / 1000));
    }
  }
  function readyCount() {
    return state.concepts.filter((c) => c.image).length;
  }
  function benchStatus() {
    const n = state.concepts.length, r = readyCount();
    if (!n || r >= n) return '';
    return r ? `${r} of ${n} drawn. The rest are on the bench.` : 'Drawing your five concepts. About a minute each, all at once.';
  }

  function renderSkeletons() {
    if (!grid) return;
    grid.innerHTML = '';
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // shuffle-by-rotation so consecutive runs open on different pieces
    const start = Math.floor(Date.now() / 1000) % PROOF_WALL.length;
    for (let i = 0; i < 5; i++) {   // MUST match the engine's N_CONCEPTS = 5
      const d = document.createElement('div');
      d.className = 'concept concept--loading';
      d.style.setProperty('--i', i);
      const [name, cap] = PROOF_WALL[(start + i) % PROOF_WALL.length];
      d.innerHTML =
        '<img class="proof__img" src="assets/opt/' + name + '-700.webp" alt="">' +
        '<figcaption class="proof__cap">' + cap + '<span>Made on Dame Court</span></figcaption>';
      grid.appendChild(d);
    }
    if (proofTimer) clearInterval(proofTimer);
    if (!reduced) {
      let step = 0;
      proofTimer = setInterval(() => {
        step += 1;
        const tiles = grid.querySelectorAll('.concept--loading');
        if (!tiles.length) { clearInterval(proofTimer); proofTimer = null; return; }
        tiles.forEach((tile, i) => {
          const [name, cap] = PROOF_WALL[(start + i + step) % PROOF_WALL.length];
          const im = tile.querySelector('.proof__img');
          const fc = tile.querySelector('.proof__cap');
          if (!im) return;
          im.style.opacity = 0;
          setTimeout(() => {
            im.src = (window.BJ_FILES||'assets/opt/') + name + '-700.webp';
            if (fc) fc.firstChild.textContent = cap;
            im.onload = () => { im.style.opacity = 1; };
          }, 300);
        });
      }, 2200);
    }
  }

  function renderConcepts() {
    if (!grid) return;
    if (!state.concepts.some((c) => c.job && !c.image) && proofTimer) { clearInterval(proofTimer); proofTimer = null; }
    grid.innerHTML = '';
    const benchStart = Math.floor(Date.now() / 1000) % PROOF_WALL.length;
    state.concepts.forEach((c, i) => {
      if (c.job && !c.image) {
        // still on the bench: a piece from the case, and what is happening to theirs
        const d = document.createElement('div');
        d.className = 'concept concept--loading concept--bench';
        d.style.setProperty('--i', i);
        const [name, cap] = PROOF_WALL[(benchStart + i) % PROOF_WALL.length];
        const line = BENCH_LINES[(i + Math.floor(Date.now() / 8000)) % BENCH_LINES.length];
        d.innerHTML =
          '<img class="proof__img" src="' + (window.BJ_FILES||'assets/opt/') + name + '-700.webp" alt="">' +
          '<figcaption class="proof__cap">' + cap + '<span>Made on Dame Court</span></figcaption>' +
          '<span class="bench__line"><span class="bench__dot"></span>' + (c.title || 'Concept ' + (i + 1)) + ' · ' + line + '…</span>';
        grid.appendChild(d);
        return;
      }
      const fig = document.createElement('figure');
      fig.className = 'concept';
      fig.style.setProperty('--i', i);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'concept__btn' + (c.image ? '' : ' concept__btn--text');
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', `${c.title || 'Concept ' + (i + 1)} — tap to shortlist`);

      if (c.image) {
        const im = document.createElement('img');
        im.src = c.image;
        im.alt = c.title || `Concept ${i + 1}`;
        im.loading = 'lazy';
        btn.appendChild(im);
      } else {
        // No renderer configured — the written direction IS the concept, and
        // it still carries the consultation. Never show an empty tile.
        const body = document.createElement('span');
        body.className = 'concept__text';
        const h = document.createElement('strong');
        h.textContent = c.title || `Concept ${i + 1}`;
        body.appendChild(h);
        if (c.pitch) {
          const p = document.createElement('span');
          p.className = 'concept__pitch';
          p.textContent = c.pitch;
          body.appendChild(p);
        }
        if (c.spec) {
          const s = document.createElement('span');
          s.className = 'concept__spec';
          s.textContent = c.spec;
          body.appendChild(s);
        }
        btn.appendChild(body);
      }

      const tick = document.createElement('span');
      tick.className = 'concept__tick';
      tick.setAttribute('aria-hidden', 'true');
      btn.appendChild(tick);

      btn.addEventListener('click', () => {
        const on = btn.getAttribute('aria-pressed') === 'true';
        btn.setAttribute('aria-pressed', String(!on));
        if (on) state.chosen.delete(i); else state.chosen.add(i);
        const cont = $('[data-to-consult]');
        if (cont) cont.disabled = state.chosen.size === 0;
        const cc = $('[data-chosen-count]');
        if (cc) {
          cc.textContent = state.chosen.size
            ? `${state.chosen.size} shortlisted`
            : 'Tap the ones you like';
        }
        // park on every change, not only on the way out: a customer who
        // shortlists and then closes the tab has still told us something
        parkChoice();
        // Selecting a concept opens the refine bar on it. Refining the thing
        // you just pointed at is the whole loop.
        state.refining = on ? null : i;
        renderRefineBar();
      });

      // version history — every redraw is kept, nothing is lost
      if ((c.history || []).length > 1) {
        const hist = document.createElement('span');
        hist.className = 'concept__hist';
        c.history.forEach((h, v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'concept__ver' + (v === c.at ? ' is-current' : '');
          b.textContent = 'v' + (v + 1);
          b.title = h.change || 'Original';
          b.addEventListener('click', (e) => {
            e.stopPropagation();
            c.at = v;
            c.image = h.image;
            c.prompt = h.prompt;
            renderConcepts();
            renderRefineBar();
          });
          hist.appendChild(b);
        });
        fig.appendChild(hist);
      }

      const cap = document.createElement('figcaption');
      cap.className = 'concept__cap';
      cap.textContent = c.image ? (c.title || `Concept ${i + 1}`) : '';

      fig.appendChild(btn);
      fig.appendChild(cap);
      grid.appendChild(fig);
    });
  }

  /* ── refine loop ──────────────────────────────────────────────────── */
  function renderRefineBar() {
    const bar = $('[data-refine]');
    if (!bar) return;
    const i = state.refining;
    const c = (i === null || i === undefined) ? null : state.concepts[i];
    bar.hidden = !c;
    if (!c) return;
    const label = $('[data-refine-label]');
    if (label) {
      const v = (c.history || []).length > 1 ? ` (v${(c.at || 0) + 1})` : '';
      label.textContent = `Changing “${c.title || 'Concept ' + (i + 1)}”${v}`;
    }
  }

  const refineInput = $('[data-refine-input]');
  const refineBtn = $('[data-refine-go]');

  async function doRefine() {
    // Enter in the input and a click on the button both land here — without
    // this gate a double-fire pays for two renders of the same change.
    if (refineBtn && refineBtn.disabled) return;
    const i = state.refining;
    const c = state.concepts[i];
    const change = refineInput ? refineInput.value.trim() : '';
    if (!c || !change) return;

    refineBtn.disabled = true;
    if (refineInput) refineInput.disabled = true;
    const wasLabel = refineBtn.textContent;
    refineBtn.textContent = 'Redrawing…';
    setStatus('Redrawing that one. About a minute.');
    // The wait-state lives ON the tile being redrawn (.is-refining pairs
    // with studio-brand.css) — "which of the four is changing?" was
    // unanswerable from three lines of status text above the grid.
    const tile = grid ? grid.querySelectorAll('.concept')[i] : null;
    if (tile) tile.classList.add('is-refining');

    try {
      const res = await fetch((window.BJ_ENGINE||'')+'/api/refine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: c.prompt, change, prev: c.image }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not redraw that.');
      if (!data.image && data.job) {
        data.image = await waitForJob(data.job, (sec) => {
          setStatus(`Redrawing “${c.title || 'Concept ' + (i + 1)}” · ${BENCH_LINES[Math.floor(sec / 8) % BENCH_LINES.length]}…`);
        });
      }

      c.history = c.history || [{ image: c.image, prompt: c.prompt, change: null }];
      c.history.push({ image: data.image, prompt: data.prompt, change });
      c.at = c.history.length - 1;
      c.image = data.image;
      c.prompt = data.prompt;
      state.chosen.add(i);            // a piece you've refined is one you want

      if (refineInput) refineInput.value = '';
      renderConcepts();
      renderRefineBar();
      setStatus('');
    } catch (e) {
      setStatus(e.message, 'error');
    } finally {
      refineBtn.disabled = false;
      if (refineInput) refineInput.disabled = false;
      refineBtn.textContent = wasLabel;
      const t = grid ? grid.querySelectorAll('.concept')[i] : null;
      if (t) t.classList.remove('is-refining');
    }
  }

  if (refineBtn) refineBtn.addEventListener('click', doRefine);
  if (refineInput) {
    refineInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); doRefine(); }
    });
  }
  const refineClose = $('[data-refine-close]');
  if (refineClose) {
    refineClose.addEventListener('click', () => {
      state.refining = null;
      renderRefineBar();
    });
  }

  /* ── hand-off to consultation ─────────────────────────────────────── */
  function composeBrief() {
    const val = (sel) => {
      const el = $(sel);
      return el && el.value ? el.value : null;
    };
    return [
      `Piece: ${label('shape', state.shape)}`,
      `Chain: ${label('chain', state.chain)}`,
      `Metal: ${label('material', state.material)}`,
      val('[data-size]') ? `Size: ${val('[data-size]')}` : null,
      val('[data-budget]') ? `Budget: ${val('[data-budget]')}` : null,
      val('[data-when]') ? `Timing: ${val('[data-when]')}` : null,
      state.note.trim() ? `Notes: ${state.note.trim()}` : null,
      state.referenceName ? `Reference image: ${state.referenceName}` : null,
      state.chosen.size
        ? `Shortlisted concepts: ${[...state.chosen].map((i) => i + 1).join(', ')}`
        : null,
    ].filter(Boolean).join('\n');
  }

  function saveBrief() {
    // A hand-typed edit outranks the composed text EVERYWHERE — the guard
    // used to cover only the three dropdowns, so going back to shortlist one
    // more concept silently wiped whatever the customer had written.
    const out = $('[data-brief]');
    if (briefEdited && out) {
      try { sessionStorage.setItem('bj_brief', out.value); } catch (e) {}
      return out.value;
    }
    const brief = composeBrief();
    if (out) out.value = brief;
    // survives the page change without a backend session
    try {
      sessionStorage.setItem('bj_brief', brief);
    } catch (e) { /* private mode — the textarea is still there to copy from */ }
    return brief;
  }

  // the qualifying selects rewrite the brief as they change, but never
  // clobber an edit the customer has typed into the textarea themselves
  let briefEdited = false;
  const briefBox = $('[data-brief]');
  if (briefBox) {
    briefBox.addEventListener('input', () => { briefEdited = true; });
  }
  ['[data-size]', '[data-budget]', '[data-when]'].forEach((sel) => {
    const el = $(sel);
    if (el) {
      el.addEventListener('change', () => {
        if (!briefEdited) saveBrief();
      });
    }
  });

  const toConsult = $('[data-to-consult]');
  if (toConsult) {
    toConsult.addEventListener('click', () => {
      saveBrief();
      parkChoice();   // the design has to survive the trip to the form
      go(5);
    });
  }

  // keep whatever is in the box at the moment they leave for the form
  const bookBtn = $('[data-book]');
  if (bookBtn) {
    bookBtn.addEventListener('click', () => {
      try {
        sessionStorage.setItem('bj_brief', briefBox ? briefBox.value : composeBrief());
      } catch (e) { /* nothing to carry */ }
    });
  }

  const copyBtn = $('[data-copy-brief]');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      const out = $('[data-brief]');
      if (!out) return;
      try {
        await navigator.clipboard.writeText(out.value);
        copyBtn.textContent = 'Copied';
        setTimeout(() => { copyBtn.textContent = 'Copy brief'; }, 1800);
      } catch (e) {
        out.select();
      }
    });
  }

  /* ── boot ─────────────────────────────────────────────────────────── */

  /* No drawing engine behind the shop: the four-concepts step cannot run.
     A disabled button reading "Studio offline" is a dead click in front of a
     customer, so the concepts step is taken out of the journey altogether and
     the (already working) written-brief route becomes the main road. Shape →
     Chain & Metal → Your design → Your brief → Book the consultation, with
     nothing on screen that does not do something. */
  function noEngine() {
    const b = $('[data-offline-banner]');
    if (b) b.hidden = true;
    const g = $('[data-generate]');
    if (g) g.remove();
    const dot = $('.studio__dot[data-dot="4"]');
    if (dot) {
      const sep = dot.previousElementSibling;
      if (sep && sep.classList.contains('studio__sep')) sep.remove();
      dot.remove();
    }
    // the rail would read 01 · 02 · 03 · 05 with the concepts step gone
    const css = document.createElement('style');
    css.textContent = '#main .studio__dot[data-dot="5"]::before{content:"04"}';
    document.head.appendChild(css);
    const hint = $('[data-drawings-hint]');
    if (hint) hint.remove();
    const skip = $('[data-skip-generate]');
    if (skip) {
      skip.textContent = 'Continue to your brief';
      skip.classList.add('btn--solid');
    }
    // step 5's "Back to concepts" would land on an empty step 4
    const back4 = $('[data-back="4"]');
    if (back4) {
      back4.textContent = 'Back';
      back4.setAttribute('data-back', '3');
    }
  }

  if (!(window.BJ_ENGINE || '').trim()) {
    noEngine();
  } else {
    fetch((window.BJ_ENGINE||'')+'/api/config')
      .then((r) => r.json())
      .then((cfg) => {
        if (cfg.mock) {
          const b = $('[data-mock-banner]');
          if (b) b.hidden = false;
        }
      })
      .catch(noEngine);
  }

  /* The consultation form lives in a SEPARATE closure below, so everything it
     needs about the customer's design has to be handed over deliberately.
     Reaching for `state` from down there is a ReferenceError, and it would
     fire on the single most important action on the site. */
  /* The consultation form is on a DIFFERENT PAGE (the studio page carries no
     [data-enquiry] form — verified). So window.BJStudio alone is not enough:
     it dies with the page. The choice has to be parked in sessionStorage the
     way the brief already is, or the customer's design silently fails to
     travel and the owner gets an enquiry with no picture — the exact problem
     this whole feature exists to solve.

     The image can be a data: URL of a megabyte or more. If the quota refuses
     it, park everything EXCEPT the image rather than losing the choice
     entirely: the owner still learns which of the four they picked. */
  function parkChoice() {
    try {
      const p = window.BJStudio.picked();
      const all = window.BJStudio.pickedAll();
      const payload = Object.assign({}, window.BJStudio.details(),
                                    p ? { chosen_title: p.title,
                                          chosen_number: p.number,
                                          image: p.image } : {},
                                    // EVERY shortlisted concept, not just the
                                    // first tick — the grid says "3 shortlisted"
                                    // and the owner used to receive one.
                                    all.length > 1 ? { images: all } : {});
      try {
        sessionStorage.setItem('bj_picked', JSON.stringify(payload));
      } catch (e) {
        // quota: drop the heavyweight renders first, then all images —
        // everything EXCEPT pictures still travels.
        delete payload.images;
        try {
          sessionStorage.setItem('bj_picked', JSON.stringify(payload));
        } catch (e2) {
          delete payload.image;
          sessionStorage.setItem('bj_picked', JSON.stringify(payload));
        }
      }
    } catch (e) { /* private mode — the brief textarea still carries the words */ }
  }

  /* ── send the design to the shop ─────────────────────────────────────
     The whole point of the studio: the picked concept, the choices and the
     customer's words go to billjewellers@gmail.com (and a copy to them) via
     the engine, so the piece can be sketched before they walk in. */
  const sendBtn = $('[data-send]');
  if (sendBtn) {
    const sendStatus = $('[data-send-status]');
    const val = (sel) => { const el = $(sel); return el && el.value ? el.value : null; };
    const say = (msg, kind) => {
      if (!sendStatus) return;
      sendStatus.hidden = !msg;
      sendStatus.textContent = msg || '';
      sendStatus.classList.toggle('is-error', kind === 'error');
    };
    sendBtn.addEventListener('click', async () => {
      const name = ($('[data-send-name]') || {}).value || '';
      const email = ($('[data-send-email]') || {}).value || '';
      const phone = ($('[data-send-phone]') || {}).value || '';
      if (!name.trim()) { say('Please add your name.', 'error'); $('[data-send-name]').focus(); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { say('Please enter a real email address so we can reply.', 'error'); $('[data-send-email]').focus(); return; }
      const picked = window.BJStudio.picked();
      const c = picked ? state.concepts[picked.number - 1] : null;
      const changes = c && c.history ? c.history.map((h) => h.change).filter(Boolean).join('; ') : '';
      const d = window.BJStudio.details();
      sendBtn.disabled = true;
      say('Sending your design…');
      try {
        const res = await fetch((window.BJ_ENGINE||'')+'/api/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(), email: email.trim(), phone: phone.trim(),
            shape: d.shape, chain: d.chain, material: d.material, note: d.note,
            size: val('[data-size]'), budget: val('[data-budget]'), when: val('[data-when]'),
            image: picked ? picked.image : null,
            concept: picked ? `${picked.number} · ${picked.title}` : '',
            changes,
            reference: state.reference || null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not send just now.');
        say('');
        const form = sendBtn.closest('.studio__step');
        form.querySelectorAll('.form-grid, .field, .studio__actions, .eyebrow, .studio__brief').forEach((el) => {
          if (!el.closest('[data-sent]')) el.hidden = true;
        });
        const done = $('[data-sent]');
        if (done) { done.hidden = false; done.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        try { sessionStorage.removeItem('bj_picked'); } catch (e) {}
      } catch (e) {
        say(e.message + ' You can also book a consultation and bring it in person.', 'error');
        sendBtn.disabled = false;
      }
    });
  }

  window.BJStudio = {
    park: parkChoice,

    /** The design the customer settled on, or null if they never picked one. */
    picked() {
      const i = state.chosen.size ? [...state.chosen][0]
              : (state.refining !== null ? state.refining : null);
      const c = (i === null || i === undefined) ? null : state.concepts[i];
      if (!c) return null;
      return {
        title: c.title || '',
        image: c.image || null,
        prompt: c.prompt || '',
        number: i + 1,
      };
    },
    /** Every shortlisted concept, in tick order. */
    pickedAll() {
      return [...state.chosen]
        .map((i) => {
          const c = state.concepts[i];
          return c ? { title: c.title || '', image: c.image || null,
                       number: i + 1 } : null;
        })
        .filter(Boolean);
    },
    /** The choices and words behind it, for the owner's email. */
    details() {
      return {
        brief: saveBrief(),
        shape: label('shape', state.shape),
        chain: label('chain', state.chain),
        material: label('material', state.material),
        note: (state.note || '').trim(),
      };
    },
  };

  go(1, { scroll: false });
})();
/* ── consultation enquiry ─────────────────────────────────────────────────
   The consultation form is now Shopify's own contact form: it posts straight
   to the shop and lands in billjewellers@gmail.com. No external server, so
   nothing here intercepts the submit — the old XHR posted to a backend that
   does not exist on the shop, failed, and dumped the customer into a mailto.
   All this does is carry the studio brief across in a hidden field so the
   owner still gets the design the customer built.
   ────────────────────────────────────────────────────────────────────────── */
(() => {
  'use strict';
  const carry = document.querySelector('[data-brief-carry]');
  if (!carry) return;

  let parked = null;
  try { parked = JSON.parse(sessionStorage.getItem('bj_picked') || 'null'); }
  catch (e) { parked = null; }
  if (!parked) {
    try {
      const b = sessionStorage.getItem('bj_brief');
      if (b) parked = { brief: b };
    } catch (e) { parked = null; }
  }
  if (!parked) return;

  const bits = [];
  const add = (k, v) => { if (v) bits.push(k + ': ' + String(v).slice(0, 400)); };
  add('Chosen concept', parked.chosen_title);
  add('Shape', parked.shape);
  add('Chain', parked.chain);
  add('Metal', parked.material);
  add('Size', parked.size);
  add('Budget', parked.budget);
  add('Timing', parked.when);
  add('In their words', parked.note);
  add('Brief', parked.brief);
  if (!bits.length) return;
  carry.value = bits.join('\n');

  // Once it is attached to the form the parked copy has done its job — a
  // second enquiry from this tab must not arrive carrying an old design.
  const form = carry.closest('form');
  if (form) form.addEventListener('submit', () => {
    try { sessionStorage.removeItem('bj_picked');
          sessionStorage.removeItem('bj_brief'); } catch (e) {}
  });
})();
